import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Star, MessageSquare, ExternalLink, Loader2, AlertTriangle,
  TrendingUp, Users, BarChart3, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReviewManagementTab({ token }) {
  const [status, setStatus] = useState(null);
  const [location, setLocation] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/climbo/status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
      return res.data;
    } catch (err) {
      if (err.response?.status === 401) return null;
      setError("Failed to check review management status");
      return null;
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const s = await fetchStatus();
    if (!s || !s.provisioned) {
      setLoading(false);
      return;
    }
    try {
      const [locRes, revRes] = await Promise.all([
        axios.get(`${API}/climbo/location`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/climbo/reviews`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setLocation(locRes.data);
      setReviews(revRes.data.reviews || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(null); // Not set up yet — not a real error
      } else {
        setError("Failed to load review data");
      }
    }
    setLoading(false);
  }, [token, fetchStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  // No subscription
  if (!status?.has_subscription) {
    return (
      <div data-testid="review-management-tab">
        <h1 className="text-2xl font-bold text-[#1e293b] mb-1">Review Management</h1>
        <p className="text-sm text-[#64748b] mb-6">Automated review collection & reputation monitoring</p>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-10 text-center">
            <Star className="w-12 h-12 mx-auto mb-4 text-amber-400" />
            <h3 className="text-lg font-bold text-[#1e293b] mb-2">Unlock Review Management</h3>
            <p className="text-sm text-[#64748b] max-w-md mx-auto mb-6">
              Automated review requests, reputation monitoring, and multi-platform review tracking are included with our Growth plan and higher.
            </p>
            <a
              href="https://buy.stripe.com/eVqeVfd4133Q7tnf5E0RG01"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-white text-sm"
              style={{ background: "#6366f1" }}
              data-testid="review-upgrade-btn"
            >
              <TrendingUp className="w-4 h-4" /> Upgrade to Growth — $497/mo
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Has subscription but Climbo not provisioned yet
  if (!status?.provisioned) {
    return (
      <div data-testid="review-management-tab">
        <h1 className="text-2xl font-bold text-[#1e293b] mb-1">Review Management</h1>
        <p className="text-sm text-[#64748b] mb-6">Setting up your reputation dashboard</p>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-10 text-center">
            <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin text-indigo-400" />
            <h3 className="text-lg font-bold text-[#1e293b] mb-2">Provisioning Your Account</h3>
            <p className="text-sm text-[#64748b] max-w-md mx-auto">
              We're setting up your review management platform. This usually takes a few minutes.
              If it's been more than an hour, please contact support.
            </p>
            <Button onClick={fetchData} variant="outline" className="mt-4" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" /> Check Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="review-management-tab">
        <h1 className="text-2xl font-bold text-[#1e293b] mb-1">Review Management</h1>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-10 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-amber-500" />
            <p className="text-sm text-[#64748b] mb-4">{error}</p>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const globalRating = location?.global_rating;
  const globalReviews = location?.global_reviews || 0;
  const sites = location?.sites || [];

  return (
    <div data-testid="review-management-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1e293b]">Review Management</h1>
          <p className="text-sm text-[#64748b]">Your reputation across all platforms</p>
        </div>
        <div className="flex items-center gap-2">
          {status?.climbo_login_link && (
            <a href={status.climbo_login_link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition"
              data-testid="climbo-login-link">
              <ExternalLink className="w-3.5 h-3.5" /> Open Review Dashboard
            </a>
          )}
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-50">
                <Star className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-[#64748b]">Average Rating</p>
                <p className="text-xl font-bold text-[#1e293b]" data-testid="avg-rating">
                  {globalRating ? globalRating.toFixed(1) : "—"}
                  <span className="text-sm font-normal text-[#94a3b8]">/5</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
                <MessageSquare className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-[#64748b]">Total Reviews</p>
                <p className="text-xl font-bold text-[#1e293b]" data-testid="total-reviews">{globalReviews}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-50">
                <BarChart3 className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <p className="text-xs text-[#64748b]">Connected Sites</p>
                <p className="text-xl font-bold text-[#1e293b]">{sites.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-50">
                <Users className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-[#64748b]">Subscription</p>
                <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 mt-0.5">
                  {status?.subscription_status === "active" ? "Active" : status?.subscription_status || "—"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Connected Sites */}
      {sites.length > 0 && (
        <Card className="bg-white border-slate-200 shadow-sm mb-8">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#1e293b] mb-3">Connected Review Sites</h3>
            <div className="space-y-3">
              {sites.map((site, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant="outline" className="text-xs font-medium capitalize shrink-0">
                      {site.source}
                    </Badge>
                    <span className="text-sm text-[#64748b] truncate">{site.url}</span>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#1e293b]">
                        {site.average_rating ? site.average_rating.toFixed(1) : "—"}
                      </p>
                      <p className="text-[10px] text-[#94a3b8]">{site.review_count} reviews</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Reviews */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-[#1e293b] mb-3">Recent Reviews</h3>
          {reviews.length === 0 ? (
            <p className="text-sm text-[#94a3b8] text-center py-8">No reviews collected yet. Reviews will appear here as they come in.</p>
          ) : (
            <div className="space-y-3">
              {reviews.map((review, i) => (
                <div key={review.id || i} className="p-4 rounded-lg border border-slate-100 hover:border-slate-200 transition" data-testid={`review-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-semibold text-[#1e293b]">{review.name || "Anonymous"}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className="w-3.5 h-3.5"
                              fill={s <= Math.round(review.rating || 0) ? "#FBBF24" : "none"}
                              stroke={s <= Math.round(review.rating || 0) ? "#FBBF24" : "#D1D5DB"} />
                          ))}
                        </div>
                        <Badge variant="outline" className="text-[9px] capitalize">{review.source}</Badge>
                      </div>
                    </div>
                    <span className="text-xs text-[#94a3b8] shrink-0">
                      {review.date ? new Date(review.date).toLocaleDateString() : ""}
                    </span>
                  </div>
                  <p className="text-sm text-[#475569] leading-relaxed">{review.text}</p>
                  {review.reply && (
                    <div className="mt-2 ml-4 pl-3 border-l-2 border-indigo-200">
                      <p className="text-xs font-medium text-indigo-600 mb-0.5">Your Reply</p>
                      <p className="text-xs text-[#64748b]">{review.reply}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
